import { useEffect, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { ApiManager } from '../services/apiManager';

let singletonApiManager: ApiManager | null = null;
let tokenProviderRegistered = false;

/**
 * useApiManager
 *
 * Provides a singleton instance of ApiManager for API requests.
 * Automatically manages authentication token from UserContext.
 * Ensures all API calls use the current user token.
 *
 * The API URL is automatically derived from the current hostname by prepending 'api.'
 * This works for both *.ngmeet.sangoma.com and *.meet.sangoma.com domains.
 *
 * Behavior:
 * - Standalone (PKCE) mode: keeps the existing eager `setAuthToken(idToken)`
 *   write-through so callers that read the cached header still work.
 * - Hosted (MF) mode: also registers a `tokenProvider` so every outbound
 *   request re-asks `UserContext.getIdToken()` for a fresh bearer. This is
 *   the only way to stay alive across scuba-side token refreshes.
 *
 * Usage:
 *   const apiManager = useApiManager();
 *
 * Returns:
 *   ApiManager - singleton instance with up-to-date authentication.
 */
export default function useApiManager(): ApiManager {
  const { idToken, getIdToken } = useUser();
  const prevTokenRef = useRef<string | undefined>(undefined);

  if (!singletonApiManager) {
    // API URL is automatically derived from hostname in ApiManager constructor
    singletonApiManager = new ApiManager();
  }

  // Register the per-request token provider once. The closure picks up the
  // latest `getIdToken` from `useUser()` via the ref dance below.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;

  useEffect(() => {
    if (!singletonApiManager || tokenProviderRegistered) {
      return;
    }
    singletonApiManager.setTokenProvider(() => getIdTokenRef.current());
    tokenProviderRegistered = true;
  }, []);

  if (idToken && idToken !== prevTokenRef.current) {
    singletonApiManager.setAuthToken(idToken);
    prevTokenRef.current = idToken;
  } else if (!idToken && prevTokenRef.current) {
    singletonApiManager.clearAuthToken();
    prevTokenRef.current = undefined;
  }

  return singletonApiManager;
}
